import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { HexColorPicker } from 'react-colorful';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiX, FiUser, FiMail } = FiIcons;

export default function FamilyMemberModal({ member, onClose, onSave }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    color: '#3B82F6',
    avatar: '',
    role: 'member'
  });

  const [showColorPicker, setShowColorPicker] = useState(false);

  const predefinedColors = [
    '#3B82F6', '#EF4444', '#10B981', '#F59E0B',
    '#8B5CF6', '#06B6D4', '#F97316', '#84CC16'
  ];

  const roles = [
    { value: 'parent', label: 'Elternteil (Admin-Rechte)' },
    { value: 'child', label: 'Kind' },
    { value: 'member', label: 'Familienmitglied' },
    { value: 'admin', label: 'Administrator' }
  ];

  useEffect(() => {
    if (member) {
      setFormData({
        name: member.name || '',
        email: member.email || '',
        color: member.color || '#3B82F6',
        avatar: member.avatar || '',
        role: member.role || 'member'
      });
    } else {
      // Auto-generate avatar from name
      setFormData(prev => ({
        ...prev,
        avatar: prev.name.charAt(0).toUpperCase()
      }));
    }
  }, [member]);

  useEffect(() => {
    // Update avatar when name changes
    if (formData.name && !member) {
      setFormData(prev => ({
        ...prev,
        avatar: prev.name.charAt(0).toUpperCase()
      }));
    }
  }, [formData.name, member]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;

    const memberData = {
      ...formData,
      avatar: formData.avatar || formData.name.charAt(0).toUpperCase()
    };

    onSave(memberData);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />
        
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative bg-white rounded-xl shadow-xl max-w-md w-full"
        >
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">
              {member ? 'Familienmitglied bearbeiten' : 'Neues Familienmitglied'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <SafeIcon icon={FiX} className="w-6 h-6" />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Avatar Preview */}
            <div className="flex justify-center mb-4">
              <div 
                className="w-20 h-20 rounded-full flex items-center justify-center text-white text-2xl font-bold shadow-sm"
                style={{ backgroundColor: formData.color }}
              >
                {formData.avatar || formData.name.charAt(0).toUpperCase()}
              </div>
            </div>

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SafeIcon icon={FiUser} className="text-gray-400 w-4 h-4" />
                </div>
                <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full pl-10 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Vorname"
                    required
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                E-Mail Adresse
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SafeIcon icon={FiMail} className="text-gray-400 w-4 h-4" />
                </div>
                <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full pl-10 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="name@beispiel.de"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Wird für Login und Benachrichtigungen benötigt.</p>
            </div>

            {/* Role */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rolle
              </label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {roles.map(role => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Avatar & Color Group */}
            <div className="grid grid-cols-2 gap-4">
                {/* Avatar Text */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Kürzel
                    </label>
                    <input
                        type="text"
                        value={formData.avatar}
                        onChange={(e) => setFormData({ ...formData, avatar: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-center uppercase"
                        placeholder="XY"
                        maxLength={2}
                    />
                </div>
                
                {/* Color */}
                <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">
                        Farbe
                    </label>
                    <div className="flex items-center space-x-2">
                        <button
                            type="button"
                            onClick={() => setShowColorPicker(!showColorPicker)}
                            className="w-full h-[42px] rounded-lg border border-gray-300 flex items-center justify-center text-white font-medium shadow-sm hover:opacity-90 transition-opacity"
                            style={{ backgroundColor: formData.color }}
                        >
                            {formData.color}
                        </button>
                    </div>
                </div>
            </div>

            {/* Color Picker Popover */}
            {showColorPicker && (
                <div className="absolute z-10 mt-2 bg-white p-3 rounded-lg shadow-xl border border-gray-200">
                    <HexColorPicker
                        color={formData.color}
                        onChange={(color) => setFormData({ ...formData, color })}
                    />
                    <div className="flex flex-wrap gap-2 mt-3 justify-center">
                        {predefinedColors.map(color => (
                            <button
                                key={color}
                                type="button"
                                onClick={() => setFormData({ ...formData, color })}
                                className="w-6 h-6 rounded-full border border-gray-200"
                                style={{ backgroundColor: color }}
                            />
                        ))}
                    </div>
                    <button 
                        type="button" 
                        onClick={() => setShowColorPicker(false)}
                        className="w-full mt-2 text-xs text-gray-500 hover:text-gray-800"
                    >
                        Schließen
                    </button>
                </div>
            )}

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-100 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 shadow-sm"
              >
                {member ? 'Speichern' : 'Erstellen'}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
}